import Title_ru_RU                from "@/core/misc/decorators/Title_ru_RU";
import AdditionalFields, {field}  from "@/core/misc/decorators/AdditionalFields";
import Title_ru_RU_Class          from "@/core/misc/decorators/Title_ru_RU_Class";

import TYPES                      from "@/core/services/Changelogs/types";

import Core                       from "@/core/Core";
import BaseEntity                 from "@/core/misc/BaseEntity";
import Country                    from "@/domains/Countries/Country";
import Partner                    from "@/domains/Partners/Partner";
import CompanyTaxSystem           from "@/domains/CompanyTaxSystems/CompanyTaxSystem";
import CompanyAdditionalAttribute from "@/domains/Companies/CompanyAdditionalAttribute";
import BankAccount                from "@/domains/BankAccounts/BankAccount";
import CompaniesService           from "@/domains/Companies/CompaniesService";
import ProductionSite             from "@/domains/ProductionSites/ProductionSite";
import ProductionSitesService     from "@/domains/ProductionSites/ProductionSitesService";
import EdiOperatorConnection      from "@/domains/EdiOperators/EdiOperatorConnection";
import Store                      from "@/domains/Stores/Store";
import VatRate                    from "@/core/types/VatRate";

@AdditionalFields({
  inn: field("ИНН", TYPES.STRING),
  ogrn: field("ОГРН", TYPES.STRING),
  kpp: field("КПП", TYPES.STRING),
  nameShort: field("Краткое наименование", TYPES.STRING),
  nameFull: field("Полное наименование", TYPES.STRING),
  addressPostal: field("Почтовый адрес", TYPES.STRING),
  addressDeFacto: field("Фактический адрес", TYPES.STRING),
  addressDeJure: field("Юридический адрес", TYPES.STRING),

})
@Title_ru_RU_Class('Компания')
class Company extends BaseEntity {
  static Events = {
    Created: 'company:created',
    Updated: 'company:updated',
    Deleted: 'company:deleted'
  } as const;

  /** наименование */
  @Title_ru_RU('Название', TYPES.STRING)
  public name: string;

  /** страна */
  @Title_ru_RU('Страна', TYPES.OBJECT)
  private _country: Country | null;

  /** партнер */
  @Title_ru_RU('Партнер', TYPES.OBJECT)
  public partner: Partner | null;

  /** использует EDI */
  @Title_ru_RU('Использует EDI', TYPES.BOOLEAN)
  public _isUsingEdi: boolean;
  /** EDI оператор */
  @Title_ru_RU('Связанные EDI операторы + внешний айди юрлица', TYPES.OBJECT)
  public ediConnections: EdiOperatorConnection[];

  /** использует документ заказа */
  @Title_ru_RU('Использует документ заказа', TYPES.BOOLEAN)
  public isUsingOrderDocument: boolean;

  /** система налогообложения */
  @Title_ru_RU('Система налогообложения', TYPES.OBJECT)
  public _taxSystem: CompanyTaxSystem | null;
  /** является плательщиком НДС */
  @Title_ru_RU('Плательщик НДС', TYPES.BOOLEAN)
  public _isVatPayer: boolean;
  /** ставка НДС для компании */
  /** С 2025 года, УСН подразумевает какой-то процент, потому мы решили
   * сунуть это сюда */
  @Title_ru_RU('Ставка НДС', TYPES.NUMBER)
  public vatRate: VatRate | null;

  /** дополнительные атрибуты */
  @Title_ru_RU('Дополнительные атрибуты', TYPES.OBJECT)
  public additionalAttributes: Record<string, CompanyAdditionalAttribute> | null;

  /** банковские счета */
  @Title_ru_RU('Банковские счета', TYPES.OBJECT)
  public bankAccounts: BankAccount[];

  /** id компании в базе ЛСЗ (таблица tehno.finclient) */
  @Title_ru_RU('ID компании в базе ЛСЗ', TYPES.NUMBER)
  public swFinClientId: number;

  /** склад доставки по умолчанию: подставляется в заказ при выборе
   * этого юр. лица грузополучателем */
  @Title_ru_RU('Склад доставки по умолчанию', TYPES.OBJECT)
  public defaultStore: Store | null;

  /** За каждым юр лицом закреплён свой набор площадок */
  public productionSites: Array<ProductionSite>;

  /** чтобы установить страну и получить атрибуты нужны данные страны партнера */
  static fromServer(data: any, result = new Company()): Company {
    super.fromServer(data, result);

    result._country = Core.utils.hydrate(data.country, Country.fromServer);
    result.partner = Core.utils.hydrate(data.partner, Partner.fromServer);
    result.ediConnections = Core.utils.hydrateArray(data.ediConnections, EdiOperatorConnection.fromServer);
    result._taxSystem = Core.utils.hydrate(data.taxSystem, CompanyTaxSystem.fromServer);
    result.vatRate = Core.utils.hydrate(data.vatRate, VatRate.fromServer);

    result.additionalAttributes =
      Core.utils.isObject(data.additionalAttributes)
        ? Object.fromEntries(
          Object.entries(data.additionalAttributes).map(([key, val]) => [
            key,
            Core.utils.hydrate(val, CompanyAdditionalAttribute.fromServer)
          ])
        )
        : {};

    result.bankAccounts = Core.utils.hydrateArray(data.bankAccounts, BankAccount.fromServer);

    result.defaultStore = Core.utils.hydrate(data.defaultStore, Store.fromServer);

    return result;
  }

  static async fromPartner(partner: Partner): Promise<Company> {
    const company = new this();

    company.partner = partner;
    company._country = partner.country;

    company.isVatPayer = true;

    // Склада по умолчанию у новой компании нет — отсутствие явное.
    company.defaultStore = null;

    company.additionalAttributes = await CompaniesService.getAdditionalAttributes(partner.country.code);

    return company;
  }

  get country(): Country { return this._country; }
  set country(value: Country) {
    /** атрибуты, которые появятся в интерфейсе после выбора страны */
    CompaniesService.getAdditionalAttributes(value.code)
      .then(attrs => {
        this._country = value;
        this.additionalAttributes = attrs;
      });
  }

  get taxSystem() { return this._taxSystem; }
  set taxSystem(value) {
    this._taxSystem = value;

    if (['ОСНО', 'УСН'].includes(this._taxSystem.name)) {
      this.isVatPayer = true;
    } else if (['ЕНВД', 'ЕСХН'].includes(this._taxSystem.name)) {
      this.isVatPayer = false;
    }
  }

  get isVatPayer() { return this._isVatPayer; }
  set isVatPayer(value) {
    // Только UZ и RU могут вводить НДС. Остальные пока не нужны
    if (this._country.code !== 'UZ' && this._country.code !== 'RU') return;

    this._isVatPayer = value;

    if (this._isVatPayer === false) {
      this.vatRate = VatRate.zero();
      return;
    }

    if (this.country.code === 'UZ') {
      this.vatRate = Core.dictionaries.vatRates.find(rate => rate.id === 12) ?? null;
    } else {
      this.vatRate = this._taxSystem?.vatRate ?? Core.dictionaries.vatRates.find(rate => rate.id === 22) ?? null;
    }
  }


  get isUsingEdi() { return this._isUsingEdi }
  set isUsingEdi(value) {
    this._isUsingEdi = value;

    if (!this._isUsingEdi) {
      this.ediOperator = null;
    }
  }

  /**
   * Главный налоговый/регистрационный реквизит компании (ИНН, БИН, УНП, USCC, VAT и т.п.)
   * в виде строки "<название>: <значение>". Какой именно реквизит главный — определяет
   * флаг isTin на атрибуте (приходит с бэка из company_attribute_types.is_tin),
   * один на страну.
   *
   * Форматтер значения пока есть только для РФ (utils.regional.ru.formatINN);
   * для остальных стран значение выводится как есть.
   */
  getTin(): string {
    const attribute = Object.values(this.additionalAttributes ?? {})
      .find(attr => attr.isTin);

    if (Core.utils.isNullOrUndefined(attribute)) {
      return 'Не указан';
    }

    if (this._country?.code === 'RU') {
      return `${attribute.name}: ${Core.utils.regional.ru.formatINN(attribute.value)}`;
    } else {
      return `${attribute.name}: ${attribute.value}`;
    }
  }

  /**
   * Описание для отображения в селекторах (VSelect, option-description="description").
   * Показываем главный реквизит компании.
   */
  get description(): string {
    return this.getTin();
  }

  async loadProductionSites() {
    this.productionSites = await ProductionSitesService.getList(this.partner, this);
  }

  hasProductionSites() {
    return Core.utils.isNotEmptyArray(this.productionSites);
  }

  /**
   * Ставка НДС компании. Для неплательщика — нулевая ставка, а не null:
   * потребители считают ей сумму налога, отдельная ветка на «нет ставки»
   * им не нужна.
   */
  getVatRate(): VatRate | null {
    if (this._isVatPayer === false) return VatRate.zero();
    // Иностранцы с нулевым НДС
    if (this._country.code !== Core.config.localCountry.code)  return VatRate.zero();
    // Если это ОСНО
    if (this.taxSystem?.id === 1) return this.taxSystem.vatRate;
    // Если это УСН, то ставка должна лежать в vatRate
    if (this.taxSystem?.id === 3) return this.vatRate;
    return this.vatRate;
  }
}

export default Company;